import { useRef, useState } from "react";
import { Play } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Reveal, { SectionHead } from "./Reveal";

const IMG = "https://images.unsplash.com/photo-1438232992991-995b7058bbb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxjaHVyY2glMjBjb25ncmVnYXRpb24lMjB3b3JzaGlwJTIwcGFzdG9yJTIwc2VybW9ufGVufDB8fHx8MTc4OTA1Mzk3NXww&ixlib=rb-4.1.0&q=85";
const ART_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_kingdom-reach-media/artifacts/g78pln5h_ChatGPT%20Image%20Sep%208%2C%202026%2C%2006_30_58%20AM.png";

export default function About() {
  const [open, setOpen] = useState(false);
  const bannerRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: bannerRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <SectionHead eyebrow="About" title="Meet the Minister of Social Media." />
          <Reveal delay={0.1}>
            <div className="mt-8 space-y-4 text-sm sm:text-base text-slate-300 leading-relaxed">
              <p>
                <span className="text-white font-semibold">Dr. Willie E. Rogers</span> is the founder of
                Minister of Social Media™, a Kingdom Reach Media company. His work was forged inside real
                ministry — developing and applying the digital-ministry approach at Fountain of Living Water
                Apostolic Church, the movement’s ministry laboratory.
              </p>
              <p>
                His conviction is simple: the Church does not need to become more like the world to reach the
                world. It needs to become more visible in the world — with a renewed voice, a visible presence,
                and a digital doorway for the people God is already trying to reach.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-cormorant italic text-xl text-amber-100/90 border-l-2 border-gold pl-6">
              “We help small and mid-sized churches turn Sunday ministry into a consistent digital presence
              that reaches people Monday through Saturday.”
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <button data-testid="video-intro-open-button" onClick={() => setOpen(true)}
            className="group relative block w-full text-left">
            <div className="absolute -inset-3 border border-gold/30 translate-x-4 translate-y-4 pointer-events-none" />
            <div className="relative aspect-video overflow-hidden rounded-md">
              <img src={IMG} alt="Video introduction from Dr. Willie E. Rogers"
                className="w-full h-full object-cover grayscale-[30%] transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-navy/50 group-hover:bg-navy/30 transition-colors" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-20 h-20 rounded-full bg-gold text-navy shadow-[0_0_40px_rgba(212,175,55,0.5)] transition-transform group-hover:scale-110">
                  <Play size={30} className="ml-1" fill="currentColor" />
                </span>
              </span>
              <span className="absolute bottom-4 left-5 text-[10px] tracking-[0.3em] uppercase text-white/90 font-faith">
                Watch the Video Introduction
              </span>
            </div>
          </button>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <div ref={bannerRef} data-testid="kingdom-artwork-banner"
          className="relative mt-20 left-1/2 -translate-x-1/2 w-screen h-[60vh] min-h-[24rem] overflow-hidden border-y border-gold/30">
          <motion.img src={ART_IMG} alt="Heaven's Master Blueprint for Earth — Kingdom artwork"
            style={{ y: imgY }}
            className="absolute inset-0 w-full h-[120%] object-cover object-[center_20%]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F3DF9F] via-transparent to-[#F3DF9F]" />
          <div className="absolute inset-0 bg-white/10" />
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-3xl px-6 text-center">
            <p className="font-faith text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#7C5E10]">
              Heaven's Master Blueprint for Earth
            </p>
            <p className="mt-3 font-cormorant italic text-lg sm:text-2xl text-[#0D2E63] leading-snug">
              “Your Kingdom come. Your will be done. On earth as it is in Heaven.” — Matthew 6:10
            </p>
          </div>
        </div>
      </Reveal>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="video-intro-modal" className="bg-navy-surface border-gold/30 text-white sm:max-w-3xl">
          <div className="aspect-video rounded-md overflow-hidden border border-gold/20 bg-black">
            <iframe data-testid="video-intro-iframe" className="w-full h-full"
              src="https://www.youtube.com/embed/GiSynJwS1CY?rel=0"
              title="Video introduction from Dr. Willie E. Rogers"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen />
          </div>
          <p className="text-sm text-slate-400 text-center">A personal welcome from Dr. Willie E. Rogers.</p>
        </DialogContent>
      </Dialog>
    </section>
  );
}
